/**
 * Offline-first writes. Every write is queued in localStorage (per user) and flushed in order
 * whenever we are online; a failed network call leaves the queue for the next attempt.
 */
import { supabase } from './supabase.ts';
import type { Json } from './database.types.ts';
import { dropFlushed, enqueue, parseQueue, type Op } from '../core/outbox.ts';
import { readString, writeString } from './storage.ts';

const queueKey = (userId: string) => `soile.outbox.v1.${userId}`;

type Listener = (pending: number) => void;
const listeners = new Set<Listener>();
let flushing: Promise<void> | null = null;

function load(userId: string): Op[] {
  return parseQueue(readString(queueKey(userId)));
}

function save(userId: string, queue: readonly Op[]): void {
  writeString(queueKey(userId), queue.length > 0 ? JSON.stringify(queue) : null);
  listeners.forEach((l) => l(queue.length));
}

export function pendingCount(userId: string): number {
  return load(userId).length;
}

/** Writes not yet on the server, oldest first. */
export function pendingOps(userId: string): Op[] {
  return load(userId);
}

export function onPendingChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function queueWrite(userId: string, op: Op): void {
  save(userId, enqueue(load(userId), op));
  void flush(userId);
}

interface DbError {
  code?: string;
  message: string;
}

/**
 * Auth failures (RLS denial without a session, expired or invalid JWT) clear up once the
 * session refreshes, so they are retried rather than dropped.
 */
const AUTH_ERRORS = new Set(['42501', 'PGRST301', 'PGRST302', 'PGRST303']);

/** Other Postgres/PostgREST errors are permanent (retrying will not help); anything else is network. */
function isPermanent(error: DbError): boolean {
  const code = error.code ?? '';
  return !AUTH_ERRORS.has(code) && /^(\d{5}|PGRST\d+)$/.test(code);
}

async function apply(userId: string, op: Op): Promise<DbError | null> {
  switch (op.type) {
    case 'xp':
      // The server decides the amount and ignores repeats of the same ref, so retries are safe.
      return (await supabase.rpc('award_xp', { p_reason: op.row.reason, p_ref: op.row.ref })).error;
    case 'progress':
      return (
        await supabase
          .from('lesson_progress')
          .upsert({ ...op.row, steps: op.row.steps as Json, user_id: userId }, { onConflict: 'user_id,lesson_id' })
      ).error;
    case 'card':
      // A never-reviewed card is a creation: if another device already created (and maybe
      // reviewed) the same deterministic id, keep that row instead of resetting it.
      return (
        await supabase.from('cards').upsert(
          { ...op.row, front: op.row.front as Json, back: op.row.back as Json, user_id: userId },
          { onConflict: 'id', ignoreDuplicates: op.row.reps === 0 },
        )
      ).error;
    case 'review':
      return (await supabase.from('review_log').insert({ ...op.row, user_id: userId })).error;
  }
}

async function flushNow(userId: string): Promise<void> {
  let queue = load(userId);
  if (queue.length === 0) return;
  // Without a session every write would hit RLS as anon; wait for auth to recover.
  const { data } = await supabase.auth.getSession();
  if (data.session?.user.id !== userId) return;
  while (queue.length > 0) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    const op = queue[0]!;
    const error = await apply(userId, op);
    if (error && !isPermanent(error)) return; // offline or server unreachable: retry later
    if (error) console.warn('Soile: dropped a write the server rejected', op, error.message);
    // Re-read: ops may have been queued meanwhile, and a newer snapshot may have replaced
    // the head in place — then it is kept and sent on the next iteration.
    queue = load(userId);
    if (JSON.stringify(queue[0]) === JSON.stringify(op)) {
      queue = dropFlushed(queue, 1);
      save(userId, queue);
    }
  }
}

/** Flushes the queue; concurrent calls share one run. */
export function flush(userId: string): Promise<void> {
  if (!flushing) {
    flushing = flushNow(userId).finally(() => {
      flushing = null;
    });
  }
  return flushing;
}
